import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Logo from '../components/brand/Logo.jsx'
import Icon from '../components/ui/Icon.jsx'
import { useAuth } from '../context/AuthContext.jsx'
import { useCompany } from '../context/CompanyContext.jsx'
import { useFeedback } from '../components/ui/feedback.jsx'

/*
 * Brand sidebar — DESIGN.md §10 + README C. Fixed 252px, gradient
 * #2C1C4E→#211339, logo top, nav items (SVG icon + label), a separator before
 * the secondary group, active item = rgba(255,255,255,.13) + white + left
 * violet bar, user card at the bottom.
 */
const MAIN_NAV = [
  { to: '/app/dashboard', icon: 'dashboard', key: 'nav.dashboard' },
  { to: '/app/invoices', icon: 'invoice', key: 'nav.invoices' },
  { to: '/app/articles', icon: 'article', key: 'nav.articles' },
  { to: '/app/customers', icon: 'customers', key: 'nav.customers' },
  { to: '/app/suppliers', icon: 'supplier', key: 'nav.suppliers' },
]

function initials(user) {
  const f = user?.first_name?.[0] || ''
  const l = user?.last_name?.[0] || ''
  return (f + l).toUpperCase() || (user?.email?.[0] || 'U').toUpperCase()
}

function Item({ to, icon, label, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      style={({ isActive }) => ({
        position: 'relative',
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '11px 16px', margin: '2px 12px', borderRadius: 10,
        fontSize: 14, fontWeight: isActive ? 600 : 500,
        color: isActive ? '#fff' : 'rgba(255,255,255,.66)',
        background: isActive ? 'var(--sidebar-active)' : 'transparent',
        transition: 'background .15s ease, color .15s ease',
      })}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span style={{
              position: 'absolute', left: -12, top: 8, bottom: 8, width: 3,
              borderRadius: 3, background: 'var(--color-logo-infinity-dark)',
            }} />
          )}
          <Icon name={icon} size={19} />
          {label}
        </>
      )}
    </NavLink>
  )
}

export default function Sidebar() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { isSuperAdmin } = useCompany()
  const { confirm } = useFeedback()

  // Logout is a confirmed action (DESIGN.md §8 / prototype): internal modal,
  // not a browser dialog. danger:false -> primary confirm button.
  const onLogout = async () => {
    const ok = await confirm({
      tone: 'info',
      destructive: false,
      title: t('auth.logout.title'),
      message: t('auth.logout.message'),
      confirmLabel: t('auth.logout.confirm'),
      cancelLabel: t('common.cancel'),
    })
    if (!ok) return
    logout()
    navigate('/login')
  }

  const secondary = [
    { to: '/app/settings', icon: 'building', label: t('nav.settings') },
    ...(isSuperAdmin() ? [
      { to: '/app/admin', icon: 'shield', label: t('nav.admin'), end: true },
      { to: '/app/admin/companies', icon: 'building', label: t('admin.tabs.companies') },
      { to: '/app/admin/users', icon: 'customers', label: t('admin.tabs.users') },
      { to: '/app/admin/onboarding', icon: 'check', label: t('admin.tabs.validation') },
      { to: '/app/admin/app-settings', icon: 'settings', label: t('admin.tabs.appSettings') },
      { to: '/app/admin/debug', icon: 'info', label: t('nav.debug') },
    ] : []),
    { to: '/app/help', icon: 'help', label: t('nav.help') },
  ]

  return (
    <aside style={{
      width: 'var(--sidebar-width)', flex: 'none', height: '100vh',
      position: 'sticky', top: 0,
      background: 'linear-gradient(180deg, var(--sidebar-from), var(--sidebar-to))',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ padding: '22px 20px 14px' }}>
        <Logo size={25} dark />
      </div>

      <nav style={{ flex: 1, overflowY: 'auto', paddingTop: 6 }}>
        {MAIN_NAV.map((n) => <Item key={n.to} to={n.to} icon={n.icon} label={t(n.key)} />)}
        <div style={{ height: 1, background: 'rgba(255,255,255,.08)', margin: '14px 20px' }} />
        {secondary.map((n) => <Item key={n.to} to={n.to} icon={n.icon} label={n.label} end={n.end} />)}
      </nav>

      <div style={{ padding: 14, borderTop: '1px solid rgba(255,255,255,.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '6px 6px' }}>
          <div style={{
            width: 38, height: 38, borderRadius: 11, flex: 'none',
            background: 'rgba(255,255,255,.14)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 14, fontFamily: 'var(--font-title)',
          }}>
            {initials(user)}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ color: '#fff', fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : ''}
            </div>
            <div style={{ color: 'rgba(255,255,255,.5)', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.email}
            </div>
          </div>
          <button
            onClick={onLogout}
            title={t('auth.logout.title')}
            aria-label={t('auth.logout.title')}
            style={{
              flex: 'none', width: 34, height: 34, borderRadius: 9,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent', border: 'none', color: '#b3a6d4',
              transition: 'background .15s ease, color .15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,.1)'; e.currentTarget.style.color = '#fff' }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#b3a6d4' }}
          >
            <Icon name="logout" size={18} />
          </button>
        </div>
      </div>
    </aside>
  )
}
